'use client';

import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applySummaryCompletion, applySummaryStatus, createSummaryMetadata, DEFAULT_SUMMARY_STREAM_METADATA, type SummaryStreamMetadata } from '@/components/summarizer/summary-metadata';
import type { StreamState } from '@/components/tweet/summary/types';
import { getRecordValue } from '@/lib/utils/type-guards';
import { streamConversationSummary } from './stream-summary';
export type SummaryStatus = 'idle' | 'loading' | 'streaming' | 'complete' | 'error';
interface UseConversationSummaryStreamParameters {
  canonicalTweetId: string;
  hasImages: boolean;
  summaryImageCount: number;
}
export interface ConversationSummaryStream {
  consideredTweets: number | null;
  handleSummarize: () => Promise<void>;
  isSummarizing: boolean;
  resetSummary: () => void;
  summaryState: StreamState;
  usedImages: number | null;
}
export function useConversationSummaryStream({
  canonicalTweetId,
  hasImages,
  summaryImageCount
}: UseConversationSummaryStreamParameters): ConversationSummaryStream {
  const createInitialMetadata = useCallback(() => createSummaryMetadata({
    ...DEFAULT_SUMMARY_STREAM_METADATA,
    availableImages: summaryImageCount,
    shouldIncludeImages: hasImages,
    mode: 'summary'
  }), [hasImages, summaryImageCount]);

  // Keep the latest metadata snapshot in a ref so we can reuse it across SSE callbacks without re-renders.
  const metadataReference = useRef<SummaryStreamMetadata>(createInitialMetadata());
  const [summaryState, setSummaryState] = useState<StreamState>(() => ({
    status: 'idle',
    text: '',
    error: null,
    metadata: metadataReference.current
  }));
  const [consideredTweets, setConsideredTweets] = useState<number | null>(null);
  const [usedImages, setUsedImages] = useState<number | null>(null);
  const abortReference = useRef<AbortController | null>(null);
  const isMountedReference = useRef(true);
  useEffect(() => {
    isMountedReference.current = true;
    return () => {
      isMountedReference.current = false;
      abortReference.current?.abort();
    };
  }, []);
  const mutation = useMutation({
    mutationFn: async () => {
      const controller = new AbortController();
      abortReference.current?.abort();
      abortReference.current = controller;
      metadataReference.current = createInitialMetadata();
      const aggregated: {
        text: string;
      } = {
        text: ''
      };
      try {
        await streamConversationSummary({
          canonicalTweetId,
          includeImages: hasImages,
          summaryImageCount,
          signal: controller.signal,
          callbacks: {
            onToken: payload => {
              aggregated.text = updateAggregatedText(aggregated.text, payload);
              if (isMountedReference.current) {
                setSummaryState({
                  status: 'streaming',
                  text: aggregated.text,
                  error: null,
                  metadata: metadataReference.current
                });
              }
            },
            onStatus: payload_0 => {
              metadataReference.current = applySummaryStatus(metadataReference.current, payload_0);
              const considered = getRecordValue(payload_0, 'consideredTweets');
              if (isMountedReference.current && typeof considered === 'number' && Number.isFinite(considered)) {
                setConsideredTweets(considered);
              }
              const statusIncludedImages = getRecordValue(payload_0, 'includedImages');
              if (isMountedReference.current && typeof statusIncludedImages === 'number' && Number.isFinite(statusIncludedImages)) {
                setUsedImages(statusIncludedImages);
              }
              if (isMountedReference.current) {
                setSummaryState(previous => ({
                  ...previous,
                  metadata: metadataReference.current
                }));
              }
            },
            onComplete: payload_1 => {
              if (typeof payload_1.text === 'string' && payload_1.text.length > 0) {
                aggregated.text = payload_1.text;
              }
              metadataReference.current = applySummaryCompletion(metadataReference.current, payload_1);
              if (isMountedReference.current) {
                setSummaryState({
                  status: 'complete',
                  text: aggregated.text,
                  error: null,
                  metadata: metadataReference.current
                });
                let nextIncludedImages = 0;
                const completedIncludedImages = payload_1.includedImages;
                if (typeof completedIncludedImages === 'number' && Number.isFinite(completedIncludedImages)) {
                  nextIncludedImages = completedIncludedImages;
                } else if (hasImages) {
                  nextIncludedImages = summaryImageCount;
                }
                setUsedImages(nextIncludedImages);
              }
            },
            onError: ({
              message: message_0
            }) => {
              if (isMountedReference.current) {
                setSummaryState({
                  status: 'error',
                  text: aggregated.text,
                  error: message_0,
                  metadata: metadataReference.current
                });
              }
              controller.abort();
            }
          }
        });
        if (isMountedReference.current && !aggregated.text) {
          setSummaryState({
            status: 'error',
            text: '',
            error: 'Summarizer returned an empty response.',
            metadata: metadataReference.current
          });
          throw new Error('Summarizer returned an empty response.');
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Unable to summarize conversation.';
        if (isMountedReference.current) {
          setSummaryState({
            status: 'error',
            text: '',
            error: message,
            metadata: metadataReference.current
          });
        }
        throw error;
      } finally {
        if (abortReference.current === controller) {
          abortReference.current = null;
        }
      }
    }
  });
  const resetSummary = useCallback(() => {
    metadataReference.current = createInitialMetadata();
    setSummaryState({
      status: 'idle',
      text: '',
      error: null,
      metadata: metadataReference.current
    });
    setConsideredTweets(null);
    setUsedImages(null);
  }, [createInitialMetadata]);
  const handleSummarize = useCallback(async () => {
    if (mutation.isPending) {
      return;
    }
    if (isMountedReference.current) {
      metadataReference.current = createInitialMetadata();
      setSummaryState({
        status: 'loading',
        text: '',
        error: null,
        metadata: metadataReference.current
      });
      setConsideredTweets(null);
      setUsedImages(null);
    }
    try {
      await mutation.mutateAsync();
    } catch {
      // Errors are reflected in state inside the mutation
    }
  }, [createInitialMetadata, mutation]);
  const isSummarizing = useMemo(() => summaryState.status === 'loading' || summaryState.status === 'streaming' || mutation.isPending, [mutation.isPending, summaryState.status]);
  return {
    summaryState,
    consideredTweets,
    usedImages,
    handleSummarize,
    resetSummary,
    isSummarizing
  };
}
function updateAggregatedText(current: string, payload: {
  delta?: string;
  text?: string;
}): string {
  if (typeof payload.delta === 'string') {
    return current + payload.delta;
  }
  if (typeof payload.text === 'string' && payload.text.length >= current.length) {
    return payload.text;
  }
  return current;
}
function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }
  return false;
}